import React from "react";
import ApolloReactHooks from "@apollo/react-hooks";

// attempts to login with given token
export function useLogin({
  token,
  mutationFunction,
}: {
  token: string | undefined;
  mutationFunction: (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- load-bearing: a concrete type breaks the build or changes behaviour; revisit during refactor/stack upgrade
    baseOptions?: ApolloReactHooks.MutationHookOptions<any, any> | undefined,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- load-bearing: a concrete type breaks the build or changes behaviour; revisit during refactor/stack upgrade
  ) => ApolloReactHooks.MutationTuple<any, any>;
}) {
  const [attempted, setIsAttempted] = React.useState(false);
  const [success, setIsSuccess] = React.useState(false);
  const [loading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>(undefined);

  const [loginWithToken] = mutationFunction({
    variables: {
      token: token || "",
    },
    refetchQueries: ["Viewer"],
    awaitRefetchQueries: true,
    fetchPolicy: "no-cache",
  });

  // Fire the (token-consuming) login exactly once, from an effect rather than
  // the render body. Running it during render caused it to fire twice under
  // React StrictMode (and again on any re-render); combined with the link being
  // single-use, the first run consumed the token and the second showed
  // "Session has expired".
  React.useEffect(() => {
    if (!token || attempted) {
      return;
    }

    setIsLoading(true);
    setIsAttempted(true);

    loginWithToken()
      .then((result) => {
        setIsSuccess(true);

        return result;
      })
      .finally(() => {
        setIsLoading(false);
      })
      .catch((error) => {
        setError(error.message);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const reset = () => {
    setIsSuccess(false);
    setIsLoading(false);
    setError(undefined);
  };

  return {
    attempted,
    loading,
    success,
    error,
    reset,
  };
}
